import type {Account} from "../../../../types/Account";
import {hasAdminAccess, hasSuperAdminAccess} from "../../../../util/AccountUtils";
import Busy from "../../../Busy";
import {getErrorMessageForNonStandardAndStandardResponse} from '../../../../util/NetworkErrorUtil';
import {
  BookingTransactionStatusType,
  invoiceStatusesThatCanBeCancelled,
  refundableBookingTransactionTypes,
  refundableBookingTransactionStatuses,
  supplierPaidOutBookingTransactionStatuses,
  excludedFromPayoutBookingTransactionStatuses,
} from '../../../constants/securspace-constants';
import {
  requestRetryPayment,
  requestTakePayoutOffHold,
  requestToggleIncludeRefundInPayout
} from "../../request/invoiceRequests";
import {
  ActionTypes,
  ActionDefaultIcons,
  CallbackActions,
  executeDownload,
  executeOpenTab,
} from "../../../../util/ActionUtils";
import type {ActionType, CallbackAction} from "../../../../util/ActionUtils";
import {adminInvoicesReportEndpoints} from "../../../../requests/api-endpoints";
import type {IActionItem} from "../../../deprecatedUikit/InfoCard";
import type {InvoiceTransactionVO as Invoice} from "../../../../types/model/valueObject/invoice/InvoiceTransactionVO";

type InvoiceActionDialog = typeof InvoiceActionDialogs[keyof typeof InvoiceActionDialogs];

type OnInvoiceActionCallback = (action: CallbackAction, invoice: Invoice, messageOrDialog?: string) => void;
interface PostRequestPayload {
  requester: (invoice: Invoice) => Promise<unknown>;
  successMessage: string;
}

interface DownloadPayload {
  endpoint: (invoice: Invoice) => string;
  successMessage: string;
  errorMessage: string;
}

interface OpenTabPayload {
  endpoint: (invoice: Invoice) => string;
  errorMessage: string;
}

interface DialogPayload {
  dialog: InvoiceActionDialog;
}

interface HandlerPayload {
  handleClick: (invoice: Invoice, onInvoiceAction: OnInvoiceActionCallback) => void;
}

type InvoiceActionPayload = DownloadPayload | DialogPayload | PostRequestPayload | HandlerPayload | OpenTabPayload;

interface InvoiceActionDefinition {
  test: (account: Account, invoice: Invoice) => boolean;
  actionType: ActionType;
  actionPayload: InvoiceActionPayload;
  /** onClick is omitted here because it is assigned dynamically in getInvoiceActionsItems */
  actionItem: Omit<IActionItem, 'onClick'>;
}

const InvoiceActionDialogs = Object.freeze({
  ADD_REFUND_CREDIT: 'ADD_REFUND_CREDIT',
  CREATE_REQUEST_REFUND: 'CREATE_REQUEST_REFUND',
  MANAGE_REQUEST_REFUND: 'MANAGE_REQUEST_REFUND',
  CANCEL_INVOICE: 'CANCEL_INVOICE',
  REQUEST_PUT_PAYOUT_ON_HOLD: 'REQUEST_PUT_PAYOUT_ON_HOLD',
  MARK_INVOICE_AS_PAID: 'MARK_INVOICE_AS_PAID',
} as const);

const postRequest = (invoice: Invoice, actionPayload: PostRequestPayload, onInvoiceAction: OnInvoiceActionCallback): void => {
  Busy.set(true);
  actionPayload.requester(invoice)
    .then(() => {
      onInvoiceAction(CallbackActions.SUCCESS, invoice, actionPayload.successMessage);
    })
    .catch((err) => {
      onInvoiceAction(CallbackActions.ERROR, invoice, getErrorMessageForNonStandardAndStandardResponse(err));
    })
    .finally(() => {
      Busy.set(false);
    });
};

const actionItems: InvoiceActionDefinition[] = [
  {
    // !!invoice ensures a boolean return for TS; original JS used truthy coercion (`&& invoice`)
    test: (account: Account, invoice: Invoice) => hasAdminAccess(account) && !!invoice,
    actionType: ActionTypes.DOWNLOAD,
    actionPayload: {
      endpoint: (invoice: Invoice) => {
        return adminInvoicesReportEndpoints.actions.downloadInvoice(invoice?.refundTransactionId || invoice?.transactionId)
      },
      successMessage: "Invoice downloaded successfully",
      errorMessage: "Unable to generate invoice",
    },
    actionItem: {
      label: 'Download Invoice',
    }
  },
  {
    test: (account: Account, invoice: Invoice) => hasAdminAccess(account) && !!invoice,
    actionType: ActionTypes.OPEN_TAB,
    actionPayload: {
      endpoint: (invoice: Invoice) => `/invoice/${invoice.refundForTransactionNumber || invoice.transactionNumber}`,
      errorMessage: "Unable to generate invoice",
    },
    actionItem: {
      label: 'View Invoice',
    }
  },
  {
    test: (account: Account, invoice: Invoice) => hasAdminAccess(account) && !!invoice,
    actionType: ActionTypes.DOWNLOAD,
    actionPayload: {
      endpoint: (invoice: Invoice) => adminInvoicesReportEndpoints.actions.downloadInventoryLog(invoice?.transactionNumber),
      successMessage: "Inventory log downloaded successfully",
      errorMessage: "Unable to generate inventory log",
    },
    actionItem: {
      label: 'Download Inventory Log',
    }
  },
  {
    test: (account: Account, invoice: Invoice) =>
      hasAdminAccess(account) &&
      (refundableBookingTransactionStatuses as readonly string[]).includes(invoice?.status) &&
      (refundableBookingTransactionTypes as readonly string[]).includes(invoice?.transactionType) &&
      !invoice.supplierPayoutOnHold,
    actionType: ActionTypes.DIALOG,
    actionPayload: {
      dialog: InvoiceActionDialogs.REQUEST_PUT_PAYOUT_ON_HOLD
    },
    actionItem: {
      label: 'Put Payout on Hold',
    }
  },
  {
    test: (account: Account, invoice: Invoice) =>
      hasAdminAccess(account) &&
      (refundableBookingTransactionStatuses as readonly string[]).includes(invoice?.status) &&
      (refundableBookingTransactionTypes as readonly string[]).includes(invoice?.transactionType) &&
      invoice.supplierPayoutOnHold,
    actionType: ActionTypes.POST_REQUEST,
    actionPayload: {
      requester: requestTakePayoutOffHold,
      successMessage: 'Successfully took payout off hold!',
    },
    actionItem: {
      label: 'Take Payout Off Hold',
    }
  },
  {
    test: (account: Account, invoice: Invoice) => hasAdminAccess(account) && invoice?.status === BookingTransactionStatusType.CHARGE_PENDING,
    actionType: ActionTypes.DIALOG,
    actionPayload: {
      dialog: InvoiceActionDialogs.ADD_REFUND_CREDIT,
    },
    actionItem: {
      label: 'Add Refund Credit...',
    }
  },
  {
    test: (account: Account, invoice: Invoice) => hasAdminAccess(account) && invoice.status === BookingTransactionStatusType.CHARGE_PENDING,
    actionType: ActionTypes.POST_REQUEST,
    actionPayload: {
      requester: requestRetryPayment,
      successMessage: 'Successfully retried charging failed payment!',
    },
    actionItem: {
      label: 'Charge Invoice',
    }
  },
  {
    test: (account: Account, invoice: Invoice) => hasAdminAccess(account) && invoice.status === BookingTransactionStatusType.PAYMENT_FAILED,
    actionType: ActionTypes.POST_REQUEST,
    actionPayload: {
      requester: requestRetryPayment,
      successMessage: 'Successfully retried charging failed payment!',
    },
    actionItem: {
      label: 'Retry Failed Payment',
    }
  },
  {
    test: (account: Account, invoice: Invoice) =>
      hasSuperAdminAccess(account) &&
      invoice?.transferType === 'CHARGE' &&
      ((invoice?.status === BookingTransactionStatusType.MANUAL_PAYMENT && !!invoice?.manualPaymentCompletedOn) ||
        (refundableBookingTransactionStatuses as readonly string[]).includes(invoice?.status)),
    actionType: ActionTypes.DIALOG,
    actionPayload: {
      dialog: InvoiceActionDialogs.CREATE_REQUEST_REFUND,
    },
    actionItem: {
      label: 'Refund...',
    }
  },
  {
    test: (account: Account, invoice: Invoice) =>
      hasAdminAccess(account) &&
      invoice?.transferType === 'CHARGE' &&
      (supplierPaidOutBookingTransactionStatuses as readonly string[]).includes(invoice?.status),
    actionType: ActionTypes.DIALOG,
    actionPayload: {
      dialog: InvoiceActionDialogs.CREATE_REQUEST_REFUND,
    },
    actionItem: {
      label: 'Request Refund...',
    }
  },
  {
    test: (account: Account, invoice: Invoice) => hasSuperAdminAccess(account) && invoice.status === BookingTransactionStatusType.REFUND_REQUESTED,
    actionType: ActionTypes.DIALOG,
    actionPayload: {
      dialog: InvoiceActionDialogs.MANAGE_REQUEST_REFUND,
    },
    actionItem: {
      label: 'Manage Refund Request...',
    }
  },
  {
    test: (account: Account, invoice: Invoice) => hasSuperAdminAccess(account) &&
      invoice?.transferType === 'REFUND' &&
      !invoice?.includeRefundInPayout &&
      !(excludedFromPayoutBookingTransactionStatuses as readonly string[]).includes(invoice?.status),
    actionType: ActionTypes.POST_REQUEST,
    actionPayload: {
      requester: requestToggleIncludeRefundInPayout,
      successMessage: 'Successfully included refund in payout!',
    },
    actionItem: {
      label: 'Include Refund In Payout',
    }
  },
  {
    test: (account: Account, invoice: Invoice) => hasSuperAdminAccess(account) &&
      invoice?.transferType === 'REFUND' &&
      invoice?.includeRefundInPayout &&
      !(excludedFromPayoutBookingTransactionStatuses as readonly string[]).includes(invoice?.status),
    actionType: ActionTypes.POST_REQUEST,
    actionPayload: {
      requester: requestToggleIncludeRefundInPayout,
      successMessage: 'Successfully excluded refund in payout!',
    },
    actionItem: {
      label: 'Exclude Refund From Payout',
    }
  },
  {
    test: (account: Account, invoice: Invoice) => hasSuperAdminAccess(account) && invoiceStatusesThatCanBeCancelled.includes(invoice.status),
    actionType: ActionTypes.DIALOG,
    actionPayload: {
      dialog: InvoiceActionDialogs.CANCEL_INVOICE,
    },
    actionItem: {
      label: 'Cancel Invoice...',
    }
  },
  {
    test: (account: Account, invoice: Invoice) => hasAdminAccess(account) && !!invoice,
    actionType: ActionTypes.DOWNLOAD,
    actionPayload: {
      endpoint: (invoice: Invoice) => adminInvoicesReportEndpoints.actions.downloadTransactionHistory(invoice?.transactionId),
      successMessage: "Transaction history downloaded successfully",
      errorMessage: "Unable to generate transaction history",
    },
    actionItem: {
      label: 'Download Transaction History',
    }
  },
  {
    test: (account: Account, invoice: Invoice) => hasSuperAdminAccess(account) &&
      invoice?.status === 'MANUAL_PAYMENT' && !invoice.manualPaymentCompletedOn,
    actionType: ActionTypes.DIALOG,
    actionPayload: {
      dialog: InvoiceActionDialogs.MARK_INVOICE_AS_PAID,
    },
    actionItem: {
      label: 'Mark Invoice As Paid',
    }
  },
];

/**
 * Generates a list of action items for a given invoice based on the account's permissions and the invoice's properties.
 * Each action item includes an onClick handler that executes the appropriate logic for that action, such as making API requests or opening dialogs.
 *
 * @param account The account of the currently logged-in user, used to determine which actions they have permission to perform.
 * @param invoice The invoice for which to generate action items. The properties of the invoice (such as status and type) are used to determine which actions are applicable.
 * @param onInvoiceAction A callback function that is called when an action is performed, allowing the parent component to respond to the action (e.g., by showing a success message or refreshing data).
 * @returns An array of IActionItem objects that can be rendered as action buttons or menu items for the invoice.
 *
 *
 * @example
 * const actionItems = getInvoiceActionsItems(account, invoice, onInvoiceAction);
 * actionItems.forEach(item => {
 *   // Render a button for each action item
 *   <Button onClick={item.onClick} startIcon={item.icon}>{item.label}</Button>
 * });
 */
const getInvoiceActionsItems = (account: Account, invoice: Invoice, onInvoiceAction: OnInvoiceActionCallback): IActionItem[] => {
  return actionItems
    .filter(({test}) => test(account, invoice))
    .map(({actionType, actionItem, actionPayload}) => {
      const newActionItem = {...actionItem} as IActionItem;

      // Type assertions are required here because actionPayload is a union type
      // (DownloadPayload | DialogPayload | PostRequestPayload | HandlerPayload)
      // and TS cannot narrow it via the actionType string without a tagged discriminant on the payload itself.
      switch (actionType) {
        case ActionTypes.DOWNLOAD:
          newActionItem.onClick = () => executeDownload(invoice, actionPayload as unknown as Parameters<typeof executeDownload>[1], onInvoiceAction as unknown as Parameters<typeof executeDownload>[2]);
          break;
        case ActionTypes.OPEN_TAB:
          newActionItem.onClick = () => executeOpenTab(invoice, actionPayload as unknown as Parameters<typeof executeOpenTab>[1], onInvoiceAction as unknown as Parameters<typeof executeOpenTab>[2]);
          break;
        case ActionTypes.DIALOG:
          newActionItem.onClick = () => onInvoiceAction(CallbackActions.OPEN_DIALOG, invoice, (actionPayload as DialogPayload).dialog);
          break;
        case ActionTypes.POST_REQUEST:
          newActionItem.onClick = () => postRequest(invoice, actionPayload as PostRequestPayload, onInvoiceAction);
          break;
        case ActionTypes.HANDLER:
          newActionItem.onClick = () => (actionPayload as HandlerPayload).handleClick(invoice, onInvoiceAction);
          break;
        default:
          throw new Error(`Unknown action type: ${actionType}`);
      }
      newActionItem.icon = newActionItem.icon ? newActionItem.icon : ActionDefaultIcons[actionType];
      return newActionItem;
    });
}

export {
  InvoiceActionDialogs,
  getInvoiceActionsItems
}

export type { Invoice, OnInvoiceActionCallback, InvoiceActionDialog };
